import type { Transaction, ActivityGroup, TransactionType } from '../types/transactions'
import type { Notification, NotificationGroup } from '../types/notifications'

export type ActivityIconType = 'stamp' | 'reward' | 'meal' | 'referral' | 'birthday'

export function getActivityIconType(type: TransactionType): ActivityIconType {
  switch (type) {
    case 'stamps_added':
    case 'stamp_correction':
      return 'stamp'
    case 'reward_unlocked':
    case 'reward_restored':
    case 'reward_expired':
      return 'reward'
    case 'reward_redeemed':
      return 'meal'
    case 'referral_bonus':
      return 'referral'
    case 'birthday_reward_unlocked':
      return 'birthday'
  }
}

export function groupTransactionsByDate(
  txs: Transaction[],
  labels: { today: string; yesterday: string },
  locale: string,
  now = new Date(),
): ActivityGroup[] {
  const groups: Map<string, Transaction[]> = new Map()

  const todayStr = now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const yesterdayStr = yesterday.toDateString()

  for (const tx of txs) {
    const date = new Date(tx.created_at)
    const dateStr = date.toDateString()

    let label: string
    if (dateStr === todayStr) {
      label = labels.today
    } else if (dateStr === yesterdayStr) {
      label = labels.yesterday
    } else {
      const month = date.toLocaleString(locale, { month: 'long' }).toUpperCase()
      label = `${month} ${date.getFullYear()}`
    }

    if (!groups.has(label)) groups.set(label, [])
    groups.get(label)!.push(tx)
  }

  return Array.from(groups.entries()).map(([label, entries]) => ({ label, entries }))
}

export function groupNotificationsByDate(
  notifications: Notification[],
  labels: { today: string; yesterday: string; thisWeek: string },
  locale: string,
  now = new Date(),
): NotificationGroup[] {
  const groups: Map<string, Notification[]> = new Map()

  const todayStr     = now.toDateString()
  const yesterday    = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const yesterdayStr = yesterday.toDateString()

  function calendarDaysAgo(date: Date): number {
    const nowMidnight  = new Date(now.getFullYear(),  now.getMonth(),  now.getDate())
    const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    return Math.floor((nowMidnight.getTime() - dateMidnight.getTime()) / 86_400_000)
  }

  for (const notification of notifications) {
    const date    = new Date(notification.created_at)
    const dateStr = date.toDateString()

    let label: string
    if (dateStr === todayStr) {
      label = labels.today
    } else if (dateStr === yesterdayStr) {
      label = labels.yesterday
    } else if (calendarDaysAgo(date) <= 7) {
      label = labels.thisWeek
    } else {
      const month = date.toLocaleString(locale, { month: 'short' }).toUpperCase()
      label = `${month} ${date.getDate()}, ${date.getFullYear()}`
    }

    if (!groups.has(label)) groups.set(label, [])
    groups.get(label)!.push(notification)
  }

  return Array.from(groups.entries()).map(([label, entries]) => ({ label, entries }))
}

